import { ImageName } from "@mcleod/images";
import { Component, ComponentCreator, ComponentFactory } from "../..";
import { Image } from "../../components/image/Image";
import { Tooltip } from "./Tooltip";
import { TooltipFiller } from "./TooltipFiller";
import { TooltipSource } from "./TooltipSource";
import { TooltipSourceType } from "./TooltipSourceType";
import { TooltipResolvedContent } from "./TooltipResolvedContent";

export class TooltipContentResolver {
    private filler: TooltipFiller;

    constructor(filler: TooltipFiller) {
        this.filler = filler;
    }

    private get tooltip(): Tooltip {
        return this.filler.tooltip;
    }

    private get anchorComponent(): Component {
        return this.filler.anchorComponent;
    }

    /**
     * Takes the provided component creator and creates a component from it (if necessary).
     *
     * @param anchorComponent The tooltip's anchor component, which may be used in callbacks fired during component
     *                        creation
     * @param source The provided tooltip source, which includes the content it should render
     */
    async resolveSource(anchorComponent: Component, source: TooltipSource) {
        if (source.content == null) {
            return;
        }
        if (this.contentIsAsyncFunction(source)) {
            this.provideSpinner(source.type);
            const castedContent = source.content as ((component: Component) => Promise<ComponentCreator>);
            const componentCreator = await castedContent(anchorComponent);
            await this.resolveComponent(source, componentCreator);
            this.filler.debouncedLayoutContent();
        } else if (typeof source.content === "function") {
            const castedContent = source.content as ((component: Component) => ComponentCreator);
            const componentCreator = castedContent(anchorComponent);
            await this.resolveComponent(source, componentCreator);
            // A non-async dynamic content function will not normally need to call layoutContent() here.  But call it
            // anyway to cover cases where some async function is included in a non-async dynamic content method.
            this.filler.debouncedLayoutContent();
        } else if (source.content instanceof Promise) {
            this.provideSpinner(source.type);
            const componentCreator = await source.content;
            await this.resolveComponent(source, componentCreator);
            this.filler.debouncedLayoutContent();
        } else {
            await this.resolveComponent(source, source.content);
            // Non-async dynamic content will not normally need to call layoutContent() here.  But call it
            // anyway to cover cases where some async function is included in the content generation.
            this.filler.debouncedLayoutContent();
        }
    }

    /**
     * Checks to see if the TooltipSource's content is an async function or a function that returns a Promise
     */
    private contentIsAsyncFunction(source: TooltipSource): boolean {
        return typeof source.content === "function" &&
            (source.content.constructor.name === "AsyncFunction" || source.content.toString().includes("async "));
    }

    private provideSpinner(type: TooltipSourceType) {
        const heightWidth = TooltipFiller.rightPanelSources.includes(type) ? 28 : 18;
        const image = new Image({
            name: ImageName.spinner,
            color: "primary",
            rotate: true,
            height: heightWidth,
            width: heightWidth
        });
        this.filler.setResolvedContent(new TooltipResolvedContent(type, image, this.tooltip));
    }

    private async resolveComponent(source: TooltipSource, content: ComponentCreator) {
        let component: Component;
        if (!(content instanceof Component)) {
            component = ComponentFactory.createCommon(content, null, this.anchorComponent);
        } else {
            component = content;
        }
        if (component != null) {
            this.adjustComponentForSource(component, source);
            component.styleForTooltip();
        }
        this.filler.setResolvedContent(new TooltipResolvedContent(source.type, component, this.tooltip));
    }

    private adjustComponentForSource(component: Component, source: TooltipSource) {
        if (source.type === TooltipSourceType.VALIDATION_WARNING) {
            component.color = "error";
        }
    }
}
